import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, login: () => {}, register: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorId = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Global axios 401 interceptor — if any API rejects the cookie, clear user
  // state so RequireAuth kicks the visitor to /login instead of letting them
  // sit on a half-broken page where clicks do nothing.
  useEffect(() => {
    if (interceptorId.current !== null) return;
    interceptorId.current = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && url.includes("/api/") && !url.includes("/auth/me")
            && !url.includes("/auth/login") && !url.includes("/auth/register")) {
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorId.current !== null) {
        axios.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };
  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser(data);
    return data;
  };
  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, refresh, login, register, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export { API };
